import { KeyboardEvent } from "react";

import { Chip } from "primereact/chip";

import type { CatalogTagOption } from "sj-ui-toolkit/src/client/svcContent";

import S from "./TagBrowser.module.scss";

interface TagBrowserProps {
  technologies?: CatalogTagOption[];
  languages?: CatalogTagOption[];
  onTechnologySelect?: (tech: string) => void;
  onLanguageSelect?: (lang: string) => void;
  selectedTechnologies?: string[];
  selectedLanguages?: string[];
}

const TagBrowser = ({
  technologies = [],
  languages = [],
  onTechnologySelect,
  onLanguageSelect,
  selectedTechnologies = [],
  selectedLanguages = [],
}: TagBrowserProps) => {
  const handleTechClick = (tech: string) => {
    if (onTechnologySelect) {
      onTechnologySelect(tech);
    }
  };

  const handleLangClick = (lang: string) => {
    if (onLanguageSelect) {
      onLanguageSelect(lang);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>, callback: () => void) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      callback();
    }
  };

  const isTechSelected = (tech: string) => selectedTechnologies.includes(tech);
  const isLangSelected = (lang: string) => selectedLanguages.includes(lang);

  if (technologies.length <= 1 && languages.length <= 1) {
    return null;
  }

  return (
    <div className={S.tagBrowser}>
      <h2 className="sr-only">Browse by Topic</h2>
      {technologies.length > 1 && (
        <section className={S.tagSection}>
          <h3 className={S.sectionTitle}>Browse by Technology</h3>
          <div className={S.tagList} role="group" aria-label="Technology filters">
            {technologies.map(tech => {
              const selected = isTechSelected(tech.id);
              return (
                <Chip
                  key={tech.id}
                  label={tech.displayName}
                  className={`${S.tagChip} ${selected ? S.selected : ""}`}
                  role="switch"
                  aria-checked={selected}
                  tabIndex={0}
                  onClick={() => handleTechClick(tech.id)}
                  onKeyDown={e => handleKeyDown(e, () => handleTechClick(tech.id))}
                />
              );
            })}
          </div>
        </section>
      )}

      {languages.length > 1 && (
        <section className={S.tagSection}>
          <h3 className={S.sectionTitle}>Browse by Language</h3>
          <div className={S.tagList} role="group" aria-label="Language filters">
            {languages.map(lang => {
              const selected = isLangSelected(lang.id);
              return (
                <Chip
                  key={lang.id}
                  label={lang.displayName}
                  className={`${S.tagChip} ${S.languageChip} ${selected ? S.selected : ""}`}
                  role="switch"
                  aria-checked={selected}
                  tabIndex={0}
                  onClick={() => handleLangClick(lang.id)}
                  onKeyDown={e => handleKeyDown(e, () => handleLangClick(lang.id))}
                />
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default TagBrowser;
